'use client'

import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { isGamerplugTeamMember } from '@/lib/groups'
import {
  canManageTournament,
  type ManageableTournament,
} from '@/lib/tournament-access'

/**
 * Client-side "may this user manage this event" — organizers, plus GamerPlug
 * team members for GamerPlug-created events (see `canManageTournament`).
 *
 * `resolved` is false until auth and the team lookup have both answered; gate
 * redirects on it so a team member isn't bounced away before we know they're
 * staff.
 */
export function useCanManageTournament(t: ManageableTournament): {
  canManage: boolean
  resolved: boolean
} {
  const { user, loading } = useAuth()
  const userId = user?.id ?? null
  const [team, setTeam] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!userId) {
      setTeam(false)
      return
    }
    setTeam(null)
    isGamerplugTeamMember(userId).then((v) => {
      if (!cancelled) setTeam(v)
    })
    return () => {
      cancelled = true
    }
  }, [userId])

  const canManage = useMemo(
    () => canManageTournament(t, userId, team === true),
    [t, userId, team]
  )
  return { canManage, resolved: !loading && team !== null }
}
